import { useState } from 'react';
import styled from 'styled-components';
import {
  useCreatePtoEquipmentMutation,
  useDeletePtoEquipmentMutation,
  useListPtoEquipmentQuery,
  useUpdatePtoEquipmentMutation,
  type PtoEquipment,
  type PtoEquipmentBody,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  Actions,
  Banner,
  DataTable,
  Dialog,
  Field,
  Muted,
  SmallInput,
  TableWrap,
  fmtNum,
} from '../kfo/ui';
import { AiMarkTag } from './AiMark';
import { EquipmentImportDialog } from './EquipmentImportDialog';
import { LoadChartEditor } from './LoadChartEditor';
import {
  EQUIPMENT_KINDS,
  EQ_NOTES_MAX,
  aiFieldDetails,
  aiTextRemains,
  chartFromDraft,
  chartToDraft,
  eqFieldErrors,
  equipmentErrorText,
  isStaleVersion,
  equipmentKindLabel,
  isLiftingKind,
  numToInput,
  parseDecimal,
  pointsWord,
  stripAiMark,
  textLength,
  type ChartPointDraft,
  type EquipmentAiField,
} from './model';
import {
  AiNote,
  CheckLabel,
  EditArea,
  FieldLabel,
  FormGrid,
  Hint,
  PanelTitle,
  SubTitle,
  WideSelect,
} from './ui';

/** Значок «ИИ» у параметра записи справочника (загрузка техники файлом). */
function aiMark(e: Pick<PtoEquipment, 'ai_fields'>, field: EquipmentAiField) {
  return (e.ai_fields ?? []).includes(field) ? <AiMarkTag details={aiFieldDetails(field)} /> : null;
}

/**
 * Справочник техники организации (общий для всех проектов ПТО): то, что
 * инженеры указывали в мастере «ППР по РД», предлагается при следующих ППР.
 * Правка, удаление и загрузка перечня из файла — при праве записи.
 */
export function EquipmentRegistry({ canWrite }: { canWrite: boolean }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PtoEquipment | 'new' | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  /** Сколько раз карточку открывали заново — новая форма, даже если запись та же. */
  const [reloads, setReloads] = useState(0);
  const [error, setError] = useState('');
  const {
    data,
    isFetching,
    error: loadError,
    refetch,
  } = useListPtoEquipmentQuery(undefined, {
    skip: !open,
  });
  const [remove] = useDeletePtoEquipmentMutation();
  const items = data?.items ?? [];
  const hasAi = items.some((e) => (e.ai_fields ?? []).length > 0);

  async function onDelete(e: PtoEquipment) {
    if (
      !window.confirm(
        `Удалить «${e.name}» из справочника? В уже составленных ППР техника останется.`,
      )
    ) {
      return;
    }
    setError('');
    try {
      await remove(e.id).unwrap();
    } catch (err) {
      setError(equipmentErrorText(err, 'Не удалось удалить технику.'));
    }
  }

  /** Запись изменилась, пока её правили: перечитать справочник и открыть её заново. */
  async function reopen(id: string) {
    setError('');
    try {
      const fresh = await refetch().unwrap();
      const entry = fresh.items.find((x) => x.id === id) ?? null;
      setReloads((n) => n + 1);
      setEditing(entry);
      if (!entry) setError('Запись удалили из справочника — правки не сохранены.');
    } catch (err) {
      setEditing(null);
      setError(equipmentErrorText(err, 'Не удалось перечитать справочник.'));
    }
  }

  return (
    <div data-ppr="registry">
      <Actions>
        <PanelTitle>Справочник техники организации</PanelTitle>
        {open && canWrite && (
          <>
            <Button $variant="secondary" $size="sm" type="button" onClick={() => setEditing('new')}>
              + Добавить технику
            </Button>
            <Button
              $variant="secondary"
              $size="sm"
              type="button"
              title="Перечень техники Excel или CSV: недостающие характеристики допишет ИИ, вы проверите строки перед сохранением"
              onClick={() => setImportOpen(true)}
            >
              Загрузить из файла
            </Button>
          </>
        )}
        <Button $variant="ghost" $size="sm" type="button" onClick={() => setOpen((v) => !v)}>
          {open ? 'Свернуть' : 'Показать'}
        </Button>
      </Actions>
      {!open ? (
        <Muted>
          Краны и машины, которые предлагаются в мастере «ППР по РД»: выбранная техника
          подставляется с характеристиками и ГВХ.
        </Muted>
      ) : (
        <>
          {error && <Banner $tone="danger">{error}</Banner>}
          {hasAi && (
            <Hint as="p">
              «ИИ» — справочные данные по марке техники из загрузки файлом: сверьте их с паспортом
              машины{canWrite ? ' и снимите пометку в карточке («Править»)' : ''}.
            </Hint>
          )}
          {loadError ? (
            <Banner $tone="danger">
              {equipmentErrorText(loadError, 'Не удалось загрузить справочник.')}
            </Banner>
          ) : items.length === 0 ? (
            <Muted>
              {isFetching
                ? 'Загрузка…'
                : 'Справочник пуст — техника появится здесь после первого ППР или добавьте её вручную.'}
            </Muted>
          ) : (
            <TableWrap $maxHeight="60vh">
              <DataTable>
                <thead>
                  <tr>
                    <th>Наименование</th>
                    <th>Вид</th>
                    <th>Характеристика</th>
                    <th className="num">Г/п, т</th>
                    <th className="num">Вылет, м</th>
                    <th className="num">Высота, м</th>
                    <th>ГВХ</th>
                    {canWrite && <th />}
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.id} data-equipment={e.name}>
                      <td className="wrap">
                        {e.name}
                        {e.notes ? <Muted>{e.notes}</Muted> : null}
                      </td>
                      <td className="nowrap">
                        {equipmentKindLabel(e.kind)} {aiMark(e, 'kind')}
                      </td>
                      <td className="wrap">
                        {e.characteristics ?? ''}{' '}
                        {e.characteristics && aiMark(e, 'characteristics')}
                      </td>
                      <td className="num">
                        {e.lift_capacity_t != null && aiMark(e, 'lift_capacity_t')}{' '}
                        {fmtNum(e.lift_capacity_t)}
                      </td>
                      <td className="num">
                        {e.max_reach_m != null && aiMark(e, 'max_reach_m')} {fmtNum(e.max_reach_m)}
                      </td>
                      <td className="num">
                        {e.lift_height_m != null && aiMark(e, 'lift_height_m')}{' '}
                        {fmtNum(e.lift_height_m)}
                      </td>
                      <td className="nowrap">
                        {e.load_chart.length > 0
                          ? `есть (${e.load_chart.length} ${pointsWord(e.load_chart.length)})`
                          : 'нет'}
                      </td>
                      {canWrite && (
                        <td className="nowrap">
                          <Button
                            $variant="ghost"
                            $size="sm"
                            type="button"
                            onClick={() => setEditing(e)}
                          >
                            Править
                          </Button>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            type="button"
                            title="Удалить из справочника"
                            onClick={() => onDelete(e)}
                          >
                            ✕
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableWrap>
          )}
        </>
      )}
      {editing && (
        <EquipmentDialog
          // Открыть заново (запись изменилась) — новая форма со свежими значениями.
          key={editing === 'new' ? 'new' : `${editing.id}:${editing.updated_at}:${reloads}`}
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onReload={editing === 'new' ? undefined : () => reopen(editing.id)}
        />
      )}
      <EquipmentImportDialog
        open={importOpen}
        mode="registry"
        registry={items}
        onClose={() => setImportOpen(false)}
      />
    </div>
  );
}

interface EquipmentForm {
  name: string;
  kind: string;
  characteristics: string;
  lift_capacity_t: string;
  max_reach_m: string;
  lift_height_m: string;
  load_chart: ChartPointDraft[];
  notes: string;
}

function toForm(e: PtoEquipment | null): EquipmentForm {
  return {
    name: e?.name ?? '',
    kind: e?.kind ?? 'crane_truck',
    characteristics: e?.characteristics ?? '',
    lift_capacity_t: numToInput(e?.lift_capacity_t),
    max_reach_m: numToInput(e?.max_reach_m),
    lift_height_m: numToInput(e?.lift_height_m),
    load_chart: chartToDraft(e?.load_chart ?? []),
    notes: e?.notes ?? '',
  };
}

/**
 * Создание/правка записи справочника. Пустое поле очищает значение. Пометки ИИ
 * (загрузка файлом): правка поля снимает пометку с него (характеристика — когда
 * в тексте не осталось справочных данных ИИ), «сверены с паспортом» — все
 * сразу. Правка не перетирает чужие изменения: запись, изменившаяся с момента
 * открытия, не сохраняется — её открывают заново.
 */
function EquipmentDialog({
  item,
  onClose,
  onReload,
}: {
  item: PtoEquipment | null;
  onClose: () => void;
  /** Перечитать запись и открыть её заново (после отказа «запись изменилась»). */
  onReload?: () => void;
}) {
  const [form, setForm] = useState<EquipmentForm>(() => toForm(item));
  const [initial] = useState<EquipmentForm>(() => toForm(item));
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [create, { isLoading: creating }] = useCreatePtoEquipmentMutation();
  const [update, { isLoading: updating }] = useUpdatePtoEquipmentMutation();
  const saving = creating || updating;

  const set = <K extends keyof EquipmentForm>(key: K, value: EquipmentForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const lifting =
    isLiftingKind(form.kind) ||
    !!form.lift_capacity_t ||
    !!form.max_reach_m ||
    !!form.lift_height_m ||
    form.load_chart.length > 0;

  const originalAi = item?.ai_fields ?? [];
  const changed: Record<EquipmentAiField, boolean> = {
    kind: form.kind !== initial.kind,
    characteristics:
      form.characteristics.trim() !== initial.characteristics.trim() &&
      !aiTextRemains(form.characteristics, []),
    lift_capacity_t: parseDecimal(form.lift_capacity_t) !== parseDecimal(initial.lift_capacity_t),
    max_reach_m: parseDecimal(form.max_reach_m) !== parseDecimal(initial.max_reach_m),
    lift_height_m: parseDecimal(form.lift_height_m) !== parseDecimal(initial.lift_height_m),
  };
  /** Пометки ИИ, которые останутся после сохранения. */
  const aiNow = verified ? [] : originalAi.filter((f) => !changed[f as EquipmentAiField]);
  const mark = (field: EquipmentAiField) => aiMark({ ai_fields: aiNow }, field);

  /**
   * «Сверены с паспортом»: снимаются все пометки, а из текста характеристики —
   * метка «справочно (ИИ):» (без пометки сервер её не учитывает, в ППР она
   * напечаталась бы буквально). Сняли флажок — метка возвращается, если текст
   * с тех пор не правили.
   */
  function onVerified(on: boolean) {
    setVerified(on);
    setForm((f) => {
      if (on) return { ...f, characteristics: stripAiMark(f.characteristics) };
      return f.characteristics === stripAiMark(initial.characteristics)
        ? { ...f, characteristics: initial.characteristics }
        : f;
    });
  }

  async function save() {
    const chart = chartFromDraft(form.load_chart);
    const problems = eqFieldErrors(form);
    if (chart.error) problems.push(chart.error);
    if (textLength(form.notes) > EQ_NOTES_MAX) {
      problems.push(`примечание — не длиннее ${EQ_NOTES_MAX} символов`);
    }
    if (problems.length > 0) {
      const text = problems.join('; ');
      setConflict(false);
      setError(`${text.charAt(0).toUpperCase()}${text.slice(1)}.`);
      return;
    }
    const body: PtoEquipmentBody = {
      name: form.name.trim(),
      kind: form.kind,
      characteristics: form.characteristics.trim() || null,
      lift_capacity_t: parseDecimal(form.lift_capacity_t),
      max_reach_m: parseDecimal(form.max_reach_m),
      lift_height_m: parseDecimal(form.lift_height_m),
      load_chart: chart.points,
      notes: form.notes.trim() || null,
      // Пометки передаём явно: без `ai_fields` сервер сохранил бы пометки
      // неизменённых полей — и снятые инженером («сверены с паспортом») тоже.
      ai_fields: aiNow,
    };
    setError('');
    setConflict(false);
    try {
      if (item) {
        // Запись успела измениться (её дозаполнила загрузка или правил другой
        // инженер) — сервер ответит 409 и ничего не тронет.
        await update({ id: item.id, ...body, expected_updated_at: item.updated_at }).unwrap();
      } else {
        await create(body).unwrap();
      }
      onClose();
    } catch (e) {
      // Только устаревшая версия записи (409 `stale_version`) — «Открыть заново»;
      // обычный конфликт (наименование занято) исправляют здесь же.
      const stale = item !== null && isStaleVersion(e);
      setConflict(stale);
      setError(
        equipmentErrorText(
          e,
          stale ? 'Запись изменилась, пока вы её правили.' : 'Не удалось сохранить технику.',
        ),
      );
    }
  }

  return (
    <Dialog
      open
      title={item ? `Техника: ${item.name}` : 'Новая техника'}
      onClose={onClose}
      width={720}
    >
      <FormGrid>
        <Field className="wide">
          Наименование и марка *
          <SmallInput
            value={form.name}
            placeholder="Автомобильный кран КС-55732"
            onChange={(e) => set('name', e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel>Вид {mark('kind')}</FieldLabel>
          <WideSelect value={form.kind} onChange={(e) => set('kind', e.target.value)}>
            {EQUIPMENT_KINDS.map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </WideSelect>
        </Field>
        <Field>
          <FieldLabel>Техническая характеристика {mark('characteristics')}</FieldLabel>
          <SmallInput
            value={form.characteristics}
            placeholder="Грузоподъёмность 25 т"
            onChange={(e) => set('characteristics', e.target.value)}
          />
        </Field>
        {lifting && (
          <>
            <Field>
              <FieldLabel>Грузоподъёмность max, т {mark('lift_capacity_t')}</FieldLabel>
              <SmallInput
                value={form.lift_capacity_t}
                inputMode="decimal"
                onChange={(e) => set('lift_capacity_t', e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel>Вылет max, м {mark('max_reach_m')}</FieldLabel>
              <SmallInput
                value={form.max_reach_m}
                inputMode="decimal"
                onChange={(e) => set('max_reach_m', e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel>Высота подъёма крюка, м {mark('lift_height_m')}</FieldLabel>
              <SmallInput
                value={form.lift_height_m}
                inputMode="decimal"
                onChange={(e) => set('lift_height_m', e.target.value)}
              />
            </Field>
          </>
        )}
      </FormGrid>
      {originalAi.length > 0 && (
        <AiNote>
          Значения с пометкой «ИИ» дописал ИИ по марке техники при загрузке файлом — это справочные
          данные. Сверьте их с паспортом машины: правка значения снимает пометку с поля.
          <CheckLabel>
            <input
              type="checkbox"
              checked={verified}
              onChange={(e) => onVerified(e.target.checked)}
            />
            Параметры сверены с паспортом — снять пометки ИИ
          </CheckLabel>
        </AiNote>
      )}
      {lifting && (
        <>
          <SubTitle>Грузовысотная характеристика (ГВХ)</SubTitle>
          <Hint>
            По ГВХ код находит наибольший вылет, на котором кран поднимает самый тяжёлый элемент из
            РД.
          </Hint>
          <LoadChartEditor points={form.load_chart} onChange={(p) => set('load_chart', p)} />
        </>
      )}
      <Field>
        Примечание
        <EditArea $rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>
      {error && (
        <Banner $tone={conflict ? 'warning' : 'danger'}>
          {error}
          {conflict && onReload && (
            <ConflictActions>
              <span>
                Откройте запись заново — увидите её текущие значения; ваши правки придётся внести
                ещё раз.
              </span>
              <Button $variant="secondary" $size="sm" type="button" onClick={onReload}>
                Открыть заново
              </Button>
            </ConflictActions>
          )}
        </Banner>
      )}
      <Actions>
        <Button $variant="primary" type="button" disabled={saving || conflict} onClick={save}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
        <Button $variant="ghost" type="button" disabled={saving} onClick={onClose}>
          Отмена
        </Button>
      </Actions>
    </Dialog>
  );
}

const ConflictActions = styled(Actions)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;
